import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React from 'react';
import styled from 'styled-components/native';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { StairInfo, } from '@/generated-sources/openapi';
import { InfoRow, PhotoRow, FieldCommentBox, BuildingDoorDirectionInfoRow, BuildingEntranceInfoRows, BuildingElevatorInfoRow, BuildingDoorInfoRow, PlaceDoorDirectionInfoRow, PlaceEntranceInfoRows, PlaceDoorInfoRow, PlaceNoteInfoRow, InfoRowsContainer, FloorMovementEmptyContainer, FloorMovementEmptyText, EmptyStateCard, } from './AccessibilityInfoComponents';
import { getStairDescription, FLOOR_MOVING_METHOD_LABELS, } from './PlaceInfo.utils';
// ──────────────── BuildingEntranceSection ────────────────
export function BuildingEntranceSection({ buildingDate, buildingAccessibility, compact = false, title = '건물 출입구', children, }) {
    var _a, _b;
    const Container = compact ? CompactSectionContainer : SectionContainer;
    const TitleComponent = compact ? CompactSectionTitle : SectionTitle;
    return (_jsxs(Container, { children: [_jsxs(SectionHeader, { children: [_jsx(TitleComponent, { children: title }), _jsx(SectionDate, { children: buildingDate })] }), _jsxs(SectionContent, { children: [_jsx(PhotoRow, { images: [
                            ...((_a = buildingAccessibility.entranceImages) !== null && _a !== void 0 ? _a : []),
                            ...((_b = buildingAccessibility.elevatorImages) !== null && _b !== void 0 ? _b : []),
                        ] }), _jsxs(InfoRowsContainer, { children: [_jsx(BuildingDoorDirectionInfoRow, { buildingAccessibility: buildingAccessibility }), _jsx(BuildingEntranceInfoRows, { buildingAccessibility: buildingAccessibility }), _jsx(BuildingElevatorInfoRow, { buildingAccessibility: buildingAccessibility }), _jsx(BuildingDoorInfoRow, { buildingAccessibility: buildingAccessibility })] })] }), children] }));
}
// ──────────────── BuildingEntranceEmptySection ────────────────
export function BuildingEntranceEmptySection({ compact = false, onRegister, }) {
    const Container = compact ? CompactSectionContainer : SectionContainer;
    const Title = compact ? CompactSectionTitle : SectionTitle;
    return (_jsxs(Container, { children: [_jsx(SectionHeader, { children: _jsx(Title, { children: "\uAC74\uBB3C \uCD9C\uC785\uAD6C" }) }), _jsx(EmptyStateCard, { title: '아직 등록된 건물 정보가 없어요🥲', description: '건물 정보가 없으면\n정확한 접근성을 확인할 수 없어요.', buttonText: "\uAC74\uBB3C\uC815\uBCF4 \uB4F1\uB85D", onPress: onRegister })] }));
}
// ──────────────── PlaceEntranceSection ────────────────
export function PlaceEntranceSection({ title, placeDate, placeAccessibility, compact = false, children, }) {
    var _a;
    const Container = compact ? CompactSectionContainer : SectionContainer;
    const TitleComponent = compact ? CompactSectionTitle : SectionTitle;
    const entranceComment = placeAccessibility.entranceComment;
    return (_jsxs(Container, { children: [_jsxs(SectionHeader, { children: [_jsx(TitleComponent, { children: title }), _jsx(SectionDate, { children: placeDate })] }), _jsxs(SectionContent, { children: [_jsx(PhotoRow, { images: (_a = placeAccessibility.images) !== null && _a !== void 0 ? _a : [] }), _jsxs(InfoRowsContainer, { children: [_jsx(PlaceDoorDirectionInfoRow, { placeAccessibility: placeAccessibility }), _jsx(PlaceEntranceInfoRows, { placeAccessibility: placeAccessibility }), _jsx(PlaceDoorInfoRow, { placeAccessibility: placeAccessibility }), _jsx(PlaceNoteInfoRow, { placeAccessibility: placeAccessibility })] }), entranceComment != null && entranceComment.length > 0 && (_jsx(FieldCommentBox, { comment: entranceComment, userName: placeAccessibility.registeredUserName, createdAt: placeAccessibility.createdAt }))] }), children] }));
}
// ──────────────── FloorMovementSection ────────────────
function getElevatorStairTitle(stairInfo, hasSlope) {
    if (stairInfo === StairInfo.None || stairInfo === StairInfo.Undefined) {
        if (hasSlope) {
            return '경사로 있음';
        }
        return '계단, 경사로 없음';
    }
    if (hasSlope) {
        return '계단과 경사로 있음';
    }
    return '계단 있음';
}
export function FloorMovementSection({ compact = false, placeAccessibility, }) {
    var _a, _b;
    const Container = compact ? CompactSectionContainer : SectionContainer;
    const Title = compact ? CompactSectionTitle : SectionTitle;
    const methodTypes = placeAccessibility === null || placeAccessibility === void 0 ? void 0 : placeAccessibility.floorMovingMethodTypes;
    const elevatorAccessibility = placeAccessibility === null || placeAccessibility === void 0 ? void 0 : placeAccessibility.floorMovingElevatorAccessibility;
    const elevatorComment = placeAccessibility === null || placeAccessibility === void 0 ? void 0 : placeAccessibility.floorMovingElevatorComment;
    const methodComment = placeAccessibility === null || placeAccessibility === void 0 ? void 0 : placeAccessibility.floorMovingMethodComment;
    const hasData = (methodTypes && methodTypes.length > 0) ||
        elevatorAccessibility != null ||
        (elevatorComment != null && elevatorComment.length > 0);
    if (!hasData) {
        return (_jsxs(Container, { children: [_jsx(SectionHeader, { children: _jsx(Title, { children: "\uCE35\uAC04 \uC774\uB3D9 \uC815\uBCF4" }) }), _jsx(FloorMovementEmptyContainer, { children: _jsx(FloorMovementEmptyText, { children: "\uC544\uC9C1 \uB4F1\uB85D\uB41C \uCE35\uAC04 \uC774\uB3D9 \uC815\uBCF4\uAC00 \uC5C6\uC5B4\uC694" }) })] }));
    }
    // 층간 이동 방법 텍스트
    const methodLabels = (_a = methodTypes === null || methodTypes === void 0 ? void 0 : methodTypes.map(m => FLOOR_MOVING_METHOD_LABELS[m]).join(', ')) !== null && _a !== void 0 ? _a : '';
    // 엘리베이터 접근성 텍스트
    const elevatorStairTitle = elevatorAccessibility
        ? getElevatorStairTitle(elevatorAccessibility.stairInfo, elevatorAccessibility.hasSlope)
        : '';
    const elevatorStairDescription = elevatorAccessibility
        ? getStairDescription(elevatorAccessibility.stairHeightLevel, elevatorAccessibility.stairInfo)
        : '';
    // 엘리베이터 이미지
    const elevatorImages = ((_b = elevatorAccessibility === null || elevatorAccessibility === void 0 ? void 0 : elevatorAccessibility.imageUrls) !== null && _b !== void 0 ? _b : []).map(url => ({
        imageUrl: url,
    }));
    return (_jsxs(Container, { children: [_jsx(SectionHeader, { children: _jsx(Title, { children: "\uCE35\uAC04 \uC774\uB3D9 \uC815\uBCF4" }) }), _jsxs(SectionContent, { children: [_jsx(PhotoRow, { images: elevatorImages }), _jsxs(InfoRowsContainer, { children: [methodLabels.length > 0 && (_jsx(InfoRow, { label: "\uC774\uB3D9 \uBC29\uBC95", value: methodLabels })), elevatorAccessibility && elevatorStairTitle.length > 0 && (_jsx(InfoRow, { label: "\uC5D8\uB9AC\uBCA0\uC774\uD130 \uC815\uBCF4", value: elevatorStairTitle, subValue: elevatorStairDescription }))] }), (() => {
                        const combinedComment = [methodComment, elevatorComment]
                            .filter(c => c != null && c.length > 0)
                            .join('\n\n');
                        return combinedComment.length > 0 ? (_jsx(FieldCommentBox, { comment: combinedComment, userName: placeAccessibility === null || placeAccessibility === void 0 ? void 0 : placeAccessibility.registeredUserName, createdAt: placeAccessibility === null || placeAccessibility === void 0 ? void 0 : placeAccessibility.createdAt })) : null;
                    })()] })] }));
}
// ──────────────── Styled Components ────────────────
// Full-size variant (AccessibilityTab)
const SectionContainer = styled.View `
  gap: 16px;
`;
const SectionContent = styled.View `
  gap: 16px;
`;
const SectionHeader = styled.View `
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
const SectionTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 18px;
  line-height: 26px;
  letter-spacing: -0.36px;
  color: ${color.gray90};
`;
const SectionDate = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.24px;
  color: ${color.gray50};
`;
// Compact variant (HomeTab subsection)
const CompactSectionContainer = styled.View `
  gap: 12px;
`;
const CompactSectionTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  color: ${color.gray80};
`;
